import { Footer } from '@/app/shell/Footer';
import Header from '@/app/shell/Header/Header';
import SideBar from '@/app/shell/SideBar/SideBar';
import { MobileMenuTrigger } from '@/app/shell/SideBar/SideBarMobileDrawer';
import type { SideBarItem } from '@/app/shell/SideBar/sideBarItem';
import { cn } from '@/shared/utils';
import { OnboardingOverlay } from '@/view/features/onboarding';
import { PageHeaderSlotProvider } from '@/view/shared/PageHeader';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

interface SideBarLayoutProps {
  readonly children: ReactNode;
  readonly sideBarItems: SideBarItem[];
  readonly activeItem?: string | undefined;
  readonly onSideBarItemClick: (item: SideBarItem) => void;
  readonly contentClassName?: string;
  readonly breadcrumbSlot?: ReactNode;
  readonly bannerSlot?: ReactNode;
  readonly groupLabels?: Record<string, string> | undefined;
  readonly collapsibleGroups?: readonly string[] | undefined;
  readonly sidebarFooter?: ReactNode;
}

export default function SideBarLayout({
  children,
  sideBarItems,
  activeItem,
  onSideBarItemClick,
  contentClassName,
  breadcrumbSlot,
  bannerSlot,
  groupLabels,
  collapsibleGroups,
  sidebarFooter,
}: SideBarLayoutProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [headerTarget, setHeaderTarget] = useState<HTMLDivElement | null>(null);
  const activeSideBarItem = sideBarItems.find((item) => item.key === activeItem);
  const mainRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run on route change to reset the scroll position
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
  }, [pathname]);

  return (
    <div className="flex h-full w-full overflow-hidden bg-background">
      <SideBar
        items={sideBarItems}
        activeItem={activeItem}
        onItemClick={onSideBarItemClick}
        isMobileOpen={isMobileMenuOpen}
        onMobileClose={() => setIsMobileMenuOpen(false)}
        groupLabels={groupLabels}
        collapsibleGroups={collapsibleGroups}
        footer={sidebarFooter}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="shrink-0 px-4 sm:px-6 md:px-8 lg:px-16 py-2 bg-surface/95 backdrop-blur-sm border-b border-border/50">
          <div className="flex items-center gap-3">
            <MobileMenuTrigger onClick={() => setIsMobileMenuOpen(true)} />
            <div className="flex-1">
              <Header showLogo={false} />
            </div>
          </div>
        </div>
        {breadcrumbSlot !== undefined && (
          <div className="shrink-0 px-4 sm:px-6 md:px-8 lg:px-16 py-3 bg-surface border-y border-border">
            {breadcrumbSlot}
          </div>
        )}
        {bannerSlot}
        <PageHeaderSlotProvider
          slot={{
            target: headerTarget,
            title: activeSideBarItem?.label,
            icon: activeSideBarItem?.icon,
          }}
        >
          <div className="flex min-h-0 flex-1 flex-col">
            <div ref={setHeaderTarget} className="shrink-0" />
            <main
              ref={mainRef}
              className={cn('flex-1 min-h-0', contentClassName ?? 'overflow-y-auto p-4')}
            >
              {children}
            </main>
          </div>
        </PageHeaderSlotProvider>
        <Footer />
      </div>
      <OnboardingOverlay />
    </div>
  );
}
